import { useEffect, useRef } from "react";
import WebViewer from "@pdftron/webviewer";

import {
  useMasksContext,
  useMessageContext,
  useTaskContext,
  useWebViewerContext,
  useMaskFormContext,
} from "../../providers";

import { handleDocumentLoaded, handleCustomHeader, handleAnnotationChanged } from "../../utils/pdfVisor";
import { startMaskingTool, stopMaskingTool } from "../../utils/pdfVisor";
import { getInitialRedaction } from "../../utils/pdfVisor/redactions";

import { Maybe } from "../../types/common";
import { Document } from "../../types/documents";
import { Mask, MaskType } from "../../types/masks";
import { Task } from "../../types/task";
import { RedactionInfo } from "../../types/redaction";

import useRefProp from "../../hooks/useRefProp";

import styles from "./CustomWebViewer.module.css";
import { getViewerDocument } from "../../utils/pdfVisor/dom";

interface CustomWebViewerProps {
  docSelected: Maybe<Document>;
}

const CustomWebViewer = ({ docSelected }: CustomWebViewerProps) => {
  const viewerDiv = useRef<HTMLDivElement>(null);
  const beenInitialized = useRef(false);
  const initialDocUrl = docSelected?.temporal_download_url as string;

  const { instance, setInstance, loadDocument } = useWebViewerContext();

  const { task } = useTaskContext();
  const { masks, getApiMasks } = useMasksContext();
  const { sendMessage } = useMessageContext();
  const { onAddMaskToShow, maskSelected, onResetMaskForm } = useMaskFormContext();

  const { propRef: masksRef } = useRefProp<Mask[]>(masks);
  const { propRef: docSelectedRef } = useRefProp<Maybe<Document>>(docSelected);
  
  const redactionInfoRef = useRef<RedactionInfo>(
    getInitialRedaction(task as Task, docSelected)
  );
  const maskSelectedRef = useRef<MaskType>(maskSelected);
  const freeTextRef = useRef<string>("");

  const refreshMasks = async (taskId: number, documentId: string) => {
    await getApiMasks(taskId, documentId);
  };

  const refreshNotifications = () => {
    return;
  };

  useEffect(() => {
    if (docSelected) {
      loadDocument(docSelected?.temporal_download_url);
      if (docSelected.type !== "RPP") {
        instance?.UI.disableElements(["downloadDocument"]);
      } else {
        instance?.UI.enableElements(["downloadDocument"]);
      }
      redactionInfoRef.current = getInitialRedaction(task as Task, docSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docSelected]);

  useEffect(() => {
    maskSelectedRef.current = maskSelected;
  }, [maskSelected]);

  useEffect(() => {
    if (!viewerDiv.current || beenInitialized.current) return;
    beenInitialized.current = true;
    WebViewer(
      {
        path: "/lib",
        fullAPI: true,
        enableRedaction: true,
        licenseKey: process.env.REACT_APP_APRYSE_WEBVIEWER_KEY,
        disabledElements: [
          "toolsHeader",
          "redactionPanel",
          "textPopup",
          "markReplaceTextToolButton",
          "markInsertTextToolButton",
          "freeTextToolButton",
          "freeHandHighlightToolButton",
          "freeHandToolButton",
          "highlightToolButton",
          "stickyToolButton",
          "annotationRedactButton",
          "annotationStyleEditButton",
          "annotationCommentButton",
        ],
        css: "/webViewerStyles.css",
      },
      viewerDiv.current
    ).then((instance) => {
      setInstance(instance);
      instance.UI.setLanguage('ca');
      instance.UI.loadDocument(initialDocUrl);

      task?.deleted_at !== null &&
        instance.UI.disableElements(["annotationPopup"]);

      instance.UI.Fonts.addAnnotationFont("Arial");

      const handleAddMaskClick = () => {
        const viewerDocument = getViewerDocument(instance);
        const addMaskButtonEl = viewerDocument?.querySelector('[data-element="addMaskButton"]');
        const isActive = addMaskButtonEl?.classList.contains('is-active');

        if (isActive) {
          stopMaskingTool(instance);
          return;
        }

        onAddMaskToShow(maskSelected);
        startMaskingTool(instance, {
          maskType: maskSelected,
        });
      };

      instance.UI.addEventListener(instance.UI.Events.VIEWER_LOADED, () => {
        handleCustomHeader(
          instance,
          docSelectedRef,
          (task as Task).id,
          sendMessage,
          handleAddMaskClick
        );
      });

      instance.UI.addEventListener(instance.UI.Events.DOCUMENT_LOADED, () => {
        handleDocumentLoaded(masksRef.current, instance);
        handleCustomHeader(
          instance,
          docSelectedRef,
          (task as Task).id,
          sendMessage,
          handleAddMaskClick
        );
        instance.UI.setZoomLevel(1.5);
      });

      instance.UI.addEventListener(instance.UI.Events.VIEWER_LOADED, () => {
        handleAnnotationChanged(
          instance,
          sendMessage,
          onResetMaskForm,
          redactionInfoRef,
          maskSelectedRef,
          freeTextRef,
          refreshMasks,
          refreshNotifications
        );
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div id="viewer" className={styles.documentViewer} ref={viewerDiv}></div>
  );
};
export default CustomWebViewer;
